import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';

import { Breadcrumb } from '../../../shared/models/common';

import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbsService {
  /** Subject emitting the breadcrumb hierarchy */
  private readonly breadcrumbs$: BehaviorSubject<Breadcrumb[]>;
  private breadcrumbsList: Breadcrumb[] = [];

  constructor(private router: Router) {
    this.breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);
    this.setEventListeners();
  }

  /** For getting the breadcrumbs as an observable to subscribe to */
  getBreadcrumb$(): Observable<Breadcrumb[]> {
    return this.breadcrumbs$.asObservable();
  }

  private addBreadcrumb(
    activatedRouteSnapshot: ActivatedRouteSnapshot | null,
    parentUrlSegmentsList: string[] = []
  ): void {
    if (activatedRouteSnapshot == null) {
      return;
    }

    // Construct the route URL
    const routeUrlSegmentsList = parentUrlSegmentsList.concat(activatedRouteSnapshot.url.map(url => url.path));

    // Add an element for the current route part
    if (activatedRouteSnapshot.data['breadcrumb'] != null) {
      const breadcrumb = {
        label: this.getLabel(activatedRouteSnapshot),
        url: '/' + routeUrlSegmentsList.join('/'),
      };
      this.breadcrumbsList.push(breadcrumb);
    }

    // Add another element for the next route part
    this.addBreadcrumb(activatedRouteSnapshot.firstChild, routeUrlSegmentsList);
  }

  private getLabel(route: ActivatedRouteSnapshot): string {
    const { data } = route;

    // The breadcrumb can be defined as a static string or as a function to construct the breadcrumb element out of the route data
    return typeof data['breadcrumb'] === 'function' ? data['breadcrumb'](route) : data['breadcrumb'];
  }

  /** For setting a value to the breadcrumbs$ subject
   * @param breadcrumbsList - breadcrumbs list
   */
  private setBreadcrumb$(breadcrumbsList: Breadcrumb[]): void {
    return this.breadcrumbs$.next(breadcrumbsList);
  }

  private setEventListeners(): void {
    this.router.events
      .pipe(
        // Filter the NavigationEnd events as the breadcrumb is updated only when the route reaches its end
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.breadcrumbsList = [];

        // Construct the breadcrumb hierarchy
        const root = this.router.routerState.snapshot.root;
        this.addBreadcrumb(root);

        // Emit the new hierarchy
        this.setBreadcrumb$(this.breadcrumbsList);
      });
  }
}
